// src/pages/HomePage.js
import React, { useContext, useEffect, useState, memo, useMemo, useCallback } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { db, auth } from '../utils/firebaseConfig';
import { toast } from 'react-toastify';
import { presenceTracker } from '../utils/presenceTracker';
import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  doc,
  getDoc,
} from 'firebase/firestore';
import { format, parseISO } from 'date-fns';
import {
  FiUsers,
  FiFileText,
  FiArchive,
  FiChevronUp,
  FiChevronDown,
  FiCalendar,
  FiClock,
  FiShield,
  FiUser,
  FiChevronLeft,
  FiChevronRight,
  FiStar
} from 'react-icons/fi';
import { Link } from 'react-router-dom';
import OnlineUsersTooltip from '../components/OnlineUsersTooltip';
import { 
  Box, Flex, Text, Icon, Table, Thead, Tbody, Tr, Th, Td, 
  TableContainer, VStack, HStack, Button, 
  Avatar, useColorModeValue
} from '@chakra-ui/react'; 
import HolidayCountdown from '../components/HolidayCountdown';
import { motion, AnimatePresence } from 'framer-motion';
import defaultAvatar from '../assets/default-avatar.svg'; // Make sure this path is correct



// Helper function to detect rank from name and return rank info
const getUserRankInfo = (userName) => {
  if (!userName) return null;
  
  const name = userName.toLowerCase();
  
  // Define rank structure with patterns, icons, and colors
  const ranks = [
    {
      patterns: ['chief', 'chief of police', 'police chief'],
      title: 'Chief',
      icon: FiStar,
      color: 'gold',
      level: 10
    },
    {
      patterns: ['deputy chief', 'asst chief', 'assistant chief'],
      title: 'Deputy Chief',
      icon: FiStar,
      color: 'silver',
      level: 9
    },
    {
      patterns: ['commander', 'cmdr', 'cmd'],
      title: 'Commander',
      icon: null,
      color: 'gold',
      level: 8
    },
    {
      patterns: ['captain', 'capt', 'cpt'],
      title: 'Captain',
      icon: FiStar,
      color: 'gold',
      level: 7
    },
    {
      patterns: ['lieutenant', 'lt', 'lieut'],
      title: 'Lieutenant',
      icon: FiStar,
      color: 'silver',
      level: 6
    },
    {
      patterns: ['sergeant', 'sgt', 'sarg'],
      title: 'Sergeant',
      icon: null,
      color: 'bronze',
      level: 5
    },
    {
      patterns: ['corporal', 'cpl', 'corp'],
      title: 'Corporal',
      icon: null,
      color: 'bronze',
      level: 4
    },
    {
      patterns: ['detective', 'det'],
      title: 'Detective',
      icon: null,
      color: 'blue',
      level: 3
    },
    {
      patterns: ['officer', 'ofc', 'off'],
      title: 'Officer',
      icon: null,
      color: 'blue',
      level: 2
    }
  ];
  
  // Check each rank pattern
  for (const rank of ranks) {
    for (const pattern of rank.patterns) {
      if (name.includes(pattern)) {
        return rank;
      }
    }
  }
  
  return null; // No rank detected
};

const LoadingSkeleton = () => (
  <div className="space-y-8">
    {/* Enhanced Stats Cards Skeleton with shimmer effect */}
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {[1, 2, 3, 4].map((i) => (
        <div 
          key={i} 
          className="relative overflow-hidden bg-gradient-to-br from-secondary-light/20 to-secondary-light/10 dark:from-secondary-dark/20 dark:to-secondary-dark/10 p-6 rounded-2xl backdrop-blur-sm border border-secondary-light/30 dark:border-secondary-dark/30"
        >
          {/* Shimmer effect */}
          <div 
            className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/20 dark:via-white/10 to-transparent" 
            style={{
              animation: 'shimmer 2s infinite',
              animationFillMode: 'forwards',
              transform: 'translateX(-100%)',
              willChange: 'transform'
            }}
          />
          
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-secondary-light/50 dark:bg-secondary-dark/50 rounded-xl animate-pulse"></div>
            <div className="flex-1">
              <div className="h-4 w-24 bg-secondary-light/50 dark:bg-secondary-dark/50 rounded-md animate-pulse"></div>
            </div>
          </div>
          <div className="mt-4 h-8 w-20 bg-secondary-light/50 dark:bg-secondary-dark/50 rounded-md animate-pulse"></div>
        </div>
      ))}
    </div>

    {/* Enhanced Recent Reports Skeleton */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {[1, 2].map((section) => (
        <div 
          key={section} 
          className="relative overflow-hidden bg-gradient-to-br from-primary-light/80 to-primary-light/60 dark:from-primary-dark/80 dark:to-primary-dark/60 p-6 rounded-2xl backdrop-blur-sm border border-secondary-light/30 dark:border-secondary-dark/30"
        >
          {/* Shimmer effect */}
          <div 
            className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/20 dark:via-white/10 to-transparent" 
            style={{
              animation: 'shimmer 2s infinite',
              animationFillMode: 'forwards',
              transform: 'translateX(-100%)',
              willChange: 'transform'
            }}
          />
          
          <div className="flex items-center justify-between mb-6">
            <div className="h-6 w-32 bg-secondary-light/50 dark:bg-secondary-dark/50 rounded-md animate-pulse"></div>
            <div className="h-10 w-28 bg-secondary-light/50 dark:bg-secondary-dark/50 rounded-xl animate-pulse"></div>
          </div>
          {[1, 2, 3].map((item) => (
            <div key={item} className="mb-4 p-4 bg-secondary-light/20 dark:bg-secondary-dark/20 rounded-xl backdrop-blur-sm">
              <div className="flex justify-between items-center">
                <div className="space-y-2">
                  <div className="h-4 w-32 bg-secondary-light/50 dark:bg-secondary-dark/50 rounded-md animate-pulse"></div>
                  <div className="h-3 w-24 bg-secondary-light/50 dark:bg-secondary-dark/50 rounded-md animate-pulse"></div>
                </div>
                <div className="h-10 w-10 bg-secondary-light/50 dark:bg-secondary-dark/50 rounded-xl animate-pulse"></div>
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
    
    <style jsx>{`
      @keyframes shimmer {
        100% {
          transform: translateX(100%);
        }
      }
    `}</style>
  </div>
);

const ReportSection = memo(({ title, reports, linkPrefix, currentPage, totalPages, totalReports, onPageChange }) => {
  // Add state to track direction for animation
  const [direction, setDirection] = useState(0); // 0: initial, 1: next, -1: prev

  // Add the missing functions
  const getIcon = (title) => {
    if (title.includes('Daily')) return FiFileText;
    if (title.includes('Safety')) return FiShield;
    return FiCalendar;
  };

  const formatReportDate = (report) => {
    try {
      // Handle End of Week Reports
      if (report.weekEndingDate) {
        const date = report.weekEndingDate instanceof Date 
          ? report.weekEndingDate 
          : report.weekEndingDate.toDate 
            ? report.weekEndingDate.toDate()
            : parseISO(report.weekEndingDate);
        return format(date, 'MMM d, yyyy');
      }
      // Handle Officer Safety Reports and DORs
      if (report.reportDate) {
        const date = report.reportDate instanceof Date 
          ? report.reportDate 
          : report.reportDate.toDate 
            ? report.reportDate.toDate()
            : parseISO(report.reportDate);
        return format(date, 'MMM d, yyyy');
      }
      if (report.date) {
        const date = report.date instanceof Date 
          ? report.date 
          : report.date.toDate 
            ? report.date.toDate()
            : parseISO(report.date);
        return format(date, 'MMM d, yyyy');
      }
      return 'No date';
    } catch (error) {
      console.error('Error formatting date:', error, report);
      return 'Invalid date';
    }
  };

  // Handle page change with direction tracking
  const handlePageChange = (newPage) => {
    if (newPage > currentPage) {
      setDirection(1); // Moving forward
    } else if (newPage < currentPage) {
      setDirection(-1); // Moving backward
    } else {
      setDirection(0); // Same page (shouldn't happen)
    }
    onPageChange(newPage);
  };

  // Define animation variants
  const variants = {
    enter: (direction) => ({
      x: direction > 0 ? 100 : direction < 0 ? -100 : 0,
      opacity: 0
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction) => ({
      zIndex: 0,
      x: direction < 0 ? 100 : direction > 0 ? -100 : 0,
      opacity: 0
    })
  };

  // Use modern glassmorphism design
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <Box
        borderRadius="2xl"
        overflow="hidden"
        bg="rgba(255, 255, 255, 0.06)"
        backdropFilter="blur(10px)"
        border="1px solid"
        borderColor="rgba(255, 255, 255, 0.08)"
        _dark={{
          bg: "rgba(255, 255, 255, 0.04)",
          borderColor: "rgba(255, 255, 255, 0.06)",
          boxShadow: "0 4px 24px rgba(0, 0, 0, 0.2)"
        }}
        boxShadow="0 4px 24px rgba(0, 0, 0, 0.04)"
        transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
        _hover={{
          borderColor: "rgba(255, 255, 255, 0.12)",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.06)",
          _dark: {
            borderColor: "rgba(255, 255, 255, 0.1)",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)"
          }
        }}
      >
        <HStack 
          p={4}
          justify="space-between" 
          align="center"
          bg="rgba(255, 255, 255, 0.03)"
          _dark={{
            bg: "rgba(255, 255, 255, 0.02)",
            borderColor: "rgba(255, 255, 255, 0.04)"
          }}
          borderBottom="1px solid"
          borderColor="rgba(255, 255, 255, 0.06)"
        >
          <HStack spacing={3}>
            <Box
              p={2}
              borderRadius="lg"
              bg="var(--color-accent)/10"
              color="var(--color-accent)"
            >
              <Icon 
                as={getIcon(title)} 
                boxSize={4}
              />
            </Box>
            <VStack align="start" spacing={0}>
              <Text 
                fontWeight="semibold" 
                fontSize={{ base: 'sm', md: 'md' }}
                color="var(--color-text)"
              >
                {title}
              </Text>
              <Text
                fontSize="xs"
                color="var(--color-text)"
                opacity={0.6}
              >
                {totalReports} total reports
              </Text>
            </VStack>
          </HStack>
        </HStack>

      <Box position="relative" p={{ base: 1, sm: 2 }}>
        <AnimatePresence initial={false} custom={direction} mode="wait">
          <motion.div
            key={currentPage}
            custom={direction}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{
              x: { type: "spring", stiffness: 300, damping: 30 },
              opacity: { duration: 0.2 }
            }}
          >
            <VStack spacing={{ base: 2, md: 3 }} align="stretch">
              {reports.map((report, index) => (
                <motion.div
                  key={report.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                >
                  <Link
                    to={`${linkPrefix}/${report.id}`}
                    className="group block"
                  >
                    <Box
                      p={{ base: 3, md: 4 }}
                      borderRadius="xl"
                      position="relative"
                      overflow="hidden"
                      bg="rgba(255, 255, 255, 0.04)"
                      border="1px solid"
                      borderColor="rgba(255, 255, 255, 0.06)"
                      _dark={{
                        bg: "rgba(255, 255, 255, 0.02)",
                        borderColor: "rgba(255, 255, 255, 0.04)"
                      }}
                      transition="all 0.2s cubic-bezier(0.4, 0, 0.2, 1)"
                      WebkitTapHighlightColor="transparent"
                      _hover={{
                        bg: "rgba(255, 255, 255, 0.08)",
                        borderColor: "var(--color-accent)/30",
                        transform: { base: "none", md: "translateY(-2px)" },
                        boxShadow: { base: "0 4px 12px rgba(0, 0, 0, 0.06)", md: "0 8px 24px rgba(0, 0, 0, 0.08)" },
                        _dark: {
                          bg: "rgba(255, 255, 255, 0.05)",
                          boxShadow: { base: "0 4px 12px rgba(0, 0, 0, 0.2)", md: "0 8px 24px rgba(0, 0, 0, 0.3)" }
                        }
                      }}
                      _active={{
                        transform: "scale(0.98)"
                      }}
                      _before={{
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: '-100%',
                        width: '100%',
                        height: '2px',
                        bg: 'var(--color-accent)',
                        transition: 'left 0.3s ease',
                        opacity: 0.8
                      }}
                      _groupHover={{
                        _before: {
                          left: 0
                        }
                      }}
                    >
                      <VStack align="stretch" spacing={3}>
                        <HStack justify="space-between">
                          <HStack spacing={2}>
                            <Box
                              p={1.5}
                              borderRadius="md"
                              bg="var(--color-accent)/10"
                              color="var(--color-accent)"
                              transition="all 0.2s"
                              _groupHover={{
                                bg: "var(--color-accent)/20",
                                transform: "scale(1.1)"
                              }}
                            >
                              <Icon 
                                as={FiCalendar} 
                                boxSize={3.5}
                              />
                            </Box>
                            <Text 
                              fontWeight="semibold"
                              fontSize="sm"
                              color="var(--color-text)"
                            >
                              {formatReportDate(report)}
                            </Text>
                          </HStack>
                          {report.shiftLength && (
                            <HStack 
                              spacing={1}
                              px={2}
                              py={0.5}
                              borderRadius="full"
                              bg="var(--color-accent)/10"
                              color="var(--color-accent)"
                            >
                              <Icon as={FiClock} boxSize={3} />
                              <Text fontSize="xs" fontWeight="medium">
                                {report.shiftLength}h
                              </Text>
                            </HStack>
                          )}
                        </HStack>
                        
                        {report.traineeName && (
                          <HStack spacing={2}>
                            <Box
                              p={1}
                              borderRadius="md"
                              bg="var(--color-secondary)/30"
                              color="var(--color-text)"
                              opacity={0.8}
                            >
                              <Icon as={FiUser} boxSize={3} />
                            </Box>
                            <Text 
                              fontSize="sm"
                              fontWeight="medium"
                              color="var(--color-text)"
                              opacity={0.9}
                            >
                              {report.traineeName}
                            </Text>
                          </HStack>
                        )}
                      </VStack>
                    </Box>
                  </Link>
                </motion.div>
              ))}
            </VStack>
          </motion.div>
        </AnimatePresence>
      </Box>

      {totalPages > 1 && (
        <Box 
          p={{ base: 3, md: 4 }}
          bg="rgba(255, 255, 255, 0.02)"
          _dark={{
            bg: "rgba(0, 0, 0, 0.2)",
            borderColor: "rgba(255, 255, 255, 0.04)"
          }}
          borderTop="1px solid"
          borderColor="rgba(255, 255, 255, 0.06)"
        >
          <HStack justify="center" spacing={4}>
            <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
              <Button
                size="sm"
                variant="unstyled"
                isDisabled={currentPage === 1}
                onClick={() => handlePageChange(currentPage - 1)}
                display="flex"
                alignItems="center"
                justifyContent="center"
                w={{ base: 8, md: 10 }}
                h={{ base: 8, md: 10 }}
                borderRadius="xl"
                bg="rgba(255, 255, 255, 0.05)"
                border="1px solid"
                borderColor="rgba(255, 255, 255, 0.08)"
                _hover={{
                  bg: 'var(--color-accent)/20',
                  borderColor: 'var(--color-accent)/40',
                  transform: 'translateX(-2px)'
                }}
                _disabled={{
                  opacity: 0.3,
                  cursor: 'not-allowed',
                  _hover: {
                    bg: 'rgba(255, 255, 255, 0.05)',
                    borderColor: 'rgba(255, 255, 255, 0.08)',
                    transform: 'none'
                  }
                }}
                transition="all 0.2s cubic-bezier(0.4, 0, 0.2, 1)"
              >
                <Icon as={FiChevronLeft} boxSize={{ base: 4, md: 5 }} color="var(--color-accent)" />
              </Button>
            </motion.div>
            
            <Flex 
              align="center"
              gap={2}
              px={4} 
              py={2} 
              borderRadius="xl"
              bg="rgba(255, 255, 255, 0.05)"
              border="1px solid"
              borderColor="rgba(255, 255, 255, 0.08)"
              position="relative"
              overflow="hidden"
            >
              <Box
                position="absolute"
                top={0}
                left={0}
                bottom={0}
                bg="var(--color-accent)"
                opacity={0.2}
                width={`${(currentPage / totalPages) * 100}%`}
                transition="width 0.3s ease"
              />
              <Text 
                fontSize="sm"
                fontWeight="semibold"
                color="var(--color-text)"
                position="relative"
                zIndex={1}
              >
                Page {currentPage}
              </Text>
              <Text
                fontSize="xs"
                color="var(--color-text)"
                opacity={0.6}
                position="relative"
                zIndex={1}
              >
                of {totalPages}
              </Text>
            </Flex>
            
            <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
              <Button
                size="sm"
                variant="unstyled"
                isDisabled={currentPage === totalPages}
                onClick={() => handlePageChange(currentPage + 1)}
                display="flex"
                alignItems="center"
                justifyContent="center"
                w={{ base: 8, md: 10 }}
                h={{ base: 8, md: 10 }}
                borderRadius="xl"
                bg="rgba(255, 255, 255, 0.05)"
                border="1px solid"
                borderColor="rgba(255, 255, 255, 0.08)"
                _hover={{
                  bg: 'var(--color-accent)/20',
                  borderColor: 'var(--color-accent)/40',
                  transform: 'translateX(2px)'
                }}
                _disabled={{
                  opacity: 0.3,
                  cursor: 'not-allowed',
                  _hover: {
                    bg: 'rgba(255, 255, 255, 0.05)',
                    borderColor: 'rgba(255, 255, 255, 0.08)',
                    transform: 'none'
                  }
                }}
                transition="all 0.2s cubic-bezier(0.4, 0, 0.2, 1)"
              >
                <Icon as={FiChevronRight} boxSize={{ base: 4, md: 5 }} color="var(--color-accent)" />
              </Button>
            </motion.div>
          </HStack>
        </Box>
      )}
    </Box>
    </motion.div>
  );
});

const ReportsOverview = ({ dors, officerSafetyReports, endOfWeekReports }) => {
  const { role, userName } = useContext(AuthContext);
  const [dorPage, setDorPage] = useState(1);
  const [osrPage, setOsrPage] = useState(1);
  const [eowrPage, setEowrPage] = useState(1);
  const [activeTab, setActiveTab] = useState(0);

  const ITEMS_PER_PAGE = 5;

  // Calculate analytics for each report type
  const getReportAnalytics = (reports) => {
    if (!reports || reports.length === 0) return null;

    const now = new Date();
    const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

    // Get recent reports (last 7 days)
    const recentReports = reports.filter(report => {
      const reportDate = report.date || report.reportDate || report.weekEndingDate;
      if (!reportDate) return false;
      const date = reportDate instanceof Date ? reportDate : 
                   reportDate.toDate ? reportDate.toDate() : 
                   new Date(reportDate);
      return date >= weekAgo;
    });

    // Get monthly reports for trend comparison
    const monthlyReports = reports.filter(report => {
      const reportDate = report.date || report.reportDate || report.weekEndingDate;
      if (!reportDate) return false;
      const date = reportDate instanceof Date ? reportDate : 
                   reportDate.toDate ? reportDate.toDate() : 
                   new Date(reportDate);
      return date >= monthAgo;
    });

    // Calculate average per week for trend
    const weeklyAverage = monthlyReports.length / 4; // 4 weeks in a month
    const trend = recentReports.length > weeklyAverage ? 'up' : 
                  recentReports.length < weeklyAverage ? 'down' : 'stable';

    return {
      total: reports.length,
      recent: recentReports.length,
      trend,
      weeklyAverage: Math.round(weeklyAverage * 10) / 10,
      lastSubmission: reports.length > 0 ? reports[0] : null
    };
  };

  // Calculate total pages and total reports
  const dorTotalPages = Math.max(1, Math.ceil(dors.length / ITEMS_PER_PAGE));
  const osrTotalPages = Math.max(1, Math.ceil(officerSafetyReports.length / ITEMS_PER_PAGE));
  const eowrTotalPages = Math.max(1, Math.ceil(endOfWeekReports.length / ITEMS_PER_PAGE));

  // Get paginated reports
  const getPaginatedReports = (reports, page) => {
    const startIndex = (page - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return reports.slice(startIndex, endIndex);
  };

  // Format user name to get initials and last name
  const formatUserName = (name) => {
    if (!name) return '';
    
    const nameParts = name.split(' ');
    if (nameParts.length === 1) return nameParts[0];
    
    const firstName = nameParts[0];
    const lastName = nameParts[nameParts.length - 1];
    
    return `${firstName.charAt(0)}. ${lastName}`;
  };

  // Determine the title based on user role
  const getReportsTitle = () => {
    if (role === 'trainer') {
      return `Reports Overview (Written by ${formatUserName(userName)})`;
    } else if (role === 'trainee') {
      return "Reports Overview (About you)";
    } else {
      return "Reports Overview";
    }
  };

  const reportSections = useMemo(() => {
    const dorAnalytics = getReportAnalytics(dors);
    const osrAnalytics = getReportAnalytics(officerSafetyReports);
    const eowrAnalytics = getReportAnalytics(endOfWeekReports);

    return [
      {
        title: "Daily Observation Reports",
        acronym: "DOR",
        data: dors,
        paginatedData: getPaginatedReports(dors, dorPage),
        linkPrefix: "/dor",
        currentPage: dorPage,
        totalPages: dorTotalPages,
        onPageChange: setDorPage,
        icon: FiFileText,
        show: dors?.length > 0,
        analytics: dorAnalytics,
        color: "var(--dor-badge-bg)",
        textColor: "var(--dor-badge-text)"
      },
      {
        title: "Officer Safety Reports",
        acronym: "OSR",
        data: officerSafetyReports,
        paginatedData: getPaginatedReports(officerSafetyReports, osrPage),
        linkPrefix: "/officersafetyreport",
        currentPage: osrPage,
        totalPages: osrTotalPages,
        onPageChange: setOsrPage,
        icon: FiShield,
        show: officerSafetyReports?.length > 0,
        analytics: osrAnalytics,
        color: "var(--osr-badge-bg)",
        textColor: "var(--osr-badge-text)"
      },
      {
        title: "End of Week Reports",
        acronym: "EOWR",
        data: endOfWeekReports,
        paginatedData: getPaginatedReports(endOfWeekReports, eowrPage),
        linkPrefix: "/endofweekreport",
        currentPage: eowrPage,
        totalPages: eowrTotalPages,
        onPageChange: setEowrPage,
        icon: FiCalendar,
        show: endOfWeekReports?.length > 0,
        analytics: eowrAnalytics,
        color: "var(--eowr-badge-bg)",
        textColor: "var(--eowr-badge-text)"
      },
    ].filter(section => section.show);
  }, [
    dors, officerSafetyReports, endOfWeekReports,
    dorPage, osrPage, eowrPage,
    dorTotalPages, osrTotalPages, eowrTotalPages
  ]);

  const hasReports = reportSections.length > 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.4 }}
    >
      <Box 
        borderRadius="xl"
        boxShadow="0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)"
        _dark={{
          boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2)"
        }}
        p={{ base: 2, sm: 3, md: 4, lg: 6 }}
        bg="var(--color-primary)"
        position="relative"
        overflow="hidden"
        w="full"
        minW={0}
        _hover={{
          boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
          _dark: {
            boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.4), 0 4px 6px -2px rgba(0, 0, 0, 0.3)"
          }
        }}
        transition="all 0.3s ease"
      >
        {/* Header with analytics summary */}
        <Flex mb={{ base: 4, md: 6 }} direction={{ base: 'column', md: 'row' }} justify="space-between" align={{ base: 'center', md: 'center' }} gap={4}>
          <Box flex="2" minW={0} w={{ base: 'full', md: 'auto' }}>
            <HStack spacing={3}>
              <Box
                p={2}
                borderRadius="lg"
                bg="var(--color-accent)/20"
                flexShrink={0}
              >
                <Icon 
                  as={FiFileText}
                  boxSize={5} 
                  color="var(--color-accent)"
                />
              </Box>
              <VStack align="flex-start" spacing={1} flex={1} minW={0}>
                <Text 
                  fontSize={{ base: 'md', sm: 'lg' }}
                  fontWeight="bold"
                  color="var(--color-text)"
                >
                  {getReportsTitle()}
                </Text>
                <Text 
                  fontSize={{ base: 'xs', sm: 'sm' }}
                  color="var(--color-text)"
                  opacity={0.7}
                >
                  {reportSections.length} active report type{reportSections.length !== 1 ? 's' : ''}
                </Text>
              </VStack>
            </HStack>
          </Box>

          {/* Quick stats summary - centered on mobile, right aligned on desktop */}
          {hasReports && (
            <Box flex="1" display={{ base: 'block', md: 'block' }} w={{ base: 'full', md: 'auto' }}>
              <HStack spacing={6} justify={{ base: 'center', md: 'flex-end' }} flexWrap="wrap">
                {reportSections.map((section, index) => (
                  <VStack key={index} spacing={1} align="center">
                    <HStack spacing={1}>
                      <Text fontSize={{ base: 'lg', md: 'xl' }} fontWeight="bold" color="var(--color-text)">
                        {section.analytics?.total || 0}
                      </Text>
                      {section.analytics?.trend === 'up' && (
                        <Icon as={FiChevronUp} boxSize={4} color="green.500" />
                      )}
                      {section.analytics?.trend === 'down' && (
                        <Icon as={FiChevronDown} boxSize={4} color="red.500" />
                      )}
                    </HStack>
                    <Text 
                      fontSize="xs" 
                      color="var(--color-text)" 
                      opacity={0.7}
                      textAlign="center"
                      fontWeight="medium"
                    >
                      {section.acronym}
                    </Text>
                  </VStack>
                ))}
              </HStack>
            </Box>
          )}
        </Flex>

      {!hasReports ? (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <VStack spacing={4} py={12} textAlign="center">
            <Box
              p={4}
              borderRadius="full"
              bg="var(--color-secondary)/20"
            >
              <Icon 
                as={FiFileText} 
                boxSize={8} 
                color="var(--color-accent)"
                opacity={0.6}
              />
            </Box>
            <VStack spacing={2}>
              <Text 
                fontSize="lg"
                fontWeight="semibold"
                color="var(--color-text)"
              >
                No reports yet
              </Text>
              <Text 
                fontSize="sm"
                color="var(--color-text)"
                opacity={0.6}
                maxW="sm"
                textAlign="center"
              >
                {role === 'trainee' 
                  ? "Your training reports will appear here once they're submitted by your FTO."
                  : role === 'trainer'
                  ? "Reports you create will be displayed here. Start by creating your first report."
                  : "System reports will be displayed here as they become available."
                }
              </Text>
            </VStack>
          </VStack>
        </motion.div>
      ) : (
        // Enhanced tabs with framer motion animation
        <Box width="100%">
          {/* Mobile-optimized tab selector */}
          <Box display={{ base: 'block', md: 'none' }} mb={4}>
            <Flex 
              bg="var(--color-secondary)/20" 
              p={1} 
              borderRadius="lg"
              position="relative"
            >
              {reportSections.map((section, index) => (
                <Box
                  key={index}
                  as="button"
                  onClick={() => setActiveTab(index)}
                  position="relative"
                  flex={1}
                  py={2}
                  px={2}
                  borderRadius="md"
                  bg="transparent"
                  cursor="pointer"
                  outline="none"
                  _focus={{
                    outline: 'none',
                  }}
                  color={activeTab === index ? section.textColor : 'var(--color-text)'}
                  fontWeight={activeTab === index ? 'bold' : 'medium'}
                  fontSize="sm"
                  transition="color 0.2s ease"
                  zIndex={1}
                >
                  {activeTab === index && (
                    <motion.div
                      layoutId="reports-mobile-bubble"
                      className="absolute inset-0 z-0"
                      style={{ 
                        backgroundColor: section.color,
                        borderRadius: '6px',
                      }}
                      transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                    />
                  )}
                  <Text position="relative" zIndex={1}>
                    {section.acronym}
                  </Text>
                </Box>
              ))}
            </Flex>
          </Box>

          {/* Desktop tab list with framer motion bubble animation */}
          <Box 
            display={{ base: 'none', md: 'block' }}
            overflow="visible"
            p={2}
            mx={-2}
          >
            <Flex gap={2} w="full">
              {reportSections.map((section, index) => (
                <Box
                  key={index}
                  as="button"
                  onClick={() => setActiveTab(index)}
                  position="relative"
                  px={4}
                  py={4}
                  borderRadius="lg"
                  border="2px solid transparent"
                  flex={1}
                  minW="0"
                  h="auto"
                  flexDirection="column"
                  alignItems="flex-start"
                  justifyContent="flex-start"
                  textAlign="left"
                  bg={activeTab === index ? 'transparent' : 'var(--color-secondary)/30'}
                  _hover={{
                    bg: activeTab === index ? 'transparent' : 'var(--color-secondary)/50',
                  }}
                  transition="all 0.2s ease"
                  cursor="pointer"
                  outline="none"
                  _focus={{
                    outline: 'none',
                    boxShadow: 'outline'
                  }}
                  color={activeTab === index ? section.textColor : 'var(--color-text)'}
                  zIndex={activeTab === index ? 1 : 0}
                >
                  {activeTab === index && (
                    <motion.div
                      layoutId="reports-desktop-bubble"
                      className="absolute inset-0 z-0"
                      style={{ 
                        backgroundColor: section.color,
                        borderRadius: '8px',
                        border: '2px solid var(--color-accent)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                      }}
                      transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                    />
                  )}
                  
                  <Box position="relative" zIndex={1} w="full">
                    <HStack w="full" justify="space-between" mb={2}>
                      <Icon as={section.icon} boxSize={{ base: 4, md: 5 }} />
                      {section.analytics?.trend !== 'stable' && (
                        <Icon 
                          as={section.analytics?.trend === 'up' ? FiChevronUp : FiChevronDown}
                          boxSize={3}
                          color={section.analytics?.trend === 'up' ? 'green.500' : 'red.500'}
                        />
                      )}
                    </HStack>
                    
                    <VStack align="flex-start" spacing={1} w="full">
                      <Text fontSize={{ base: "xs", md: "sm" }} fontWeight="bold" lineHeight="1.2">
                        {section.acronym}
                      </Text>
                      <Text fontSize="xs" opacity={0.8} lineHeight="1.2">
                        {section.analytics?.total || 0} total
                      </Text>
                      {section.analytics?.recent > 0 && (
                        <Text fontSize="xs" opacity={0.7} lineHeight="1.2">
                          {section.analytics.recent} this week
                        </Text>
                      )}
                    </VStack>
                  </Box>
                </Box>
              ))}
            </Flex>
          </Box>
          
          {/* Tab Panels with AnimatePresence */}
          <Box pt={4}>
            <AnimatePresence mode="wait">
              {reportSections.map((section, index) => (
                activeTab === index && (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.2 }}
                  >
                    <ReportSection
                      title={section.title}
                      reports={section.paginatedData}
                      linkPrefix={section.linkPrefix}
                      currentPage={section.currentPage}
                      totalPages={section.totalPages}
                      totalReports={section.data.length}
                      onPageChange={section.onPageChange}
                    />
                  </motion.div>
                )
              ))}
            </AnimatePresence>
          </Box>
        </Box>
      )}
      </Box>
    </motion.div>
  );
};

const StatCard = ({ icon: IconComponent, title, value, delta = 0, onlineUsers = null }) => {
  // --- Call hooks at the top level --- 
  const cardBg = useColorModeValue('var(--color-secondary)', 'var(--color-secondary)');
  const cardHoverBg = useColorModeValue('var(--color-primary)', 'var(--color-primary)');
  const increaseColor = useColorModeValue('green.500', 'green.300');
  const decreaseColor = useColorModeValue('red.500', 'red.300');
  const gradient = useColorModeValue(
    'linear(to-br, var(--color-accent-light)/20, var(--color-accent-dark)/20)',
    'linear(to-br, var(--color-accent-dark)/20, var(--color-accent-light)/20)'
  );

  // Helper to format delta display string (raw number)
  const formatDelta = () => {
    return Math.abs(delta);
  };

  // Enhanced shadow definitions
  const baseShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)';
  const darkShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.5), 0 2px 4px -1px rgba(0, 0, 0, 0.3)';
  const baseHoverShadow = '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)';
  const darkHoverShadow = '0 20px 25px -5px rgba(0, 0, 0, 0.6), 0 10px 10px -5px rgba(0, 0, 0, 0.4)';

  return (
    <motion.div
      whileHover={{ scale: 1.03, y: -4 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      style={{ width: '100%' }} // Ensure motion div takes full width
    >
      <Box
        p={{ base: 3, sm: 4, md: 5 }} // Reduced base padding
        borderRadius="xl"
        bg={cardBg}
        boxShadow={baseShadow}
        position="relative"
        overflow="hidden"
        transition="all 0.3s ease"
        _dark={{
          boxShadow: darkShadow
        }}
        _before={{ // Gradient overlay
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          bgGradient: gradient,
          opacity: 0,
          transition: 'opacity 0.3s ease'
        }}
        _hover={{
          bg: cardHoverBg,
          boxShadow: baseHoverShadow,
          _dark: {
            boxShadow: darkHoverShadow
          },
          _before: {
            opacity: 1
          }
        }}
        textAlign="left"
        h="full"
        w="full" // Ensure full width
        minW={0} // Allow shrinking
      >
        <VStack align="flex-start" spacing={{ base: 2, sm: 2, md: 3 }} h="full" position="relative">
          {/* Icon and Title Row */}
          <HStack justify="space-between" w="full" minW={0}> {/* Add minW={0} */}
            <Box
              p={{ base: 2, sm: 2, md: 3 }} // Reduced base padding
              borderRadius="lg"
              bg="var(--color-accent)/20"
              transition="all 0.3s ease"
              flexShrink={0} // Prevent icon from shrinking
              _hover={{
                bg: 'var(--color-accent)/30',
                transform: 'scale(1.1)'
              }}
            >
              <Icon 
                as={IconComponent} 
                boxSize={{ base: 4, sm: 4, md: 5 }} // Consistent base size
                color="var(--color-accent)"
              />
            </Box>
            
            {delta !== 0 && (
              <Flex align="center" gap={1} flexShrink={0}> {/* Prevent shrinking */}
                {delta > 0 ? (
                  <Icon 
                    as={FiChevronUp}
                    boxSize={{ base: 3, md: 4 }}
                    color={increaseColor}
                  />
                ) : (
                  <Icon 
                    as={FiChevronDown}
                    boxSize={{ base: 3, md: 4 }}
                    color={decreaseColor}
                  />
                )}
                <Text 
                  fontSize={{ base: 'xs', md: 'sm'}} 
                  fontWeight="medium" 
                  color={delta > 0 ? increaseColor : decreaseColor}
                >
                  {formatDelta()}
                </Text>
              </Flex>
            )}
          </HStack>

          {/* Title */}
          <Text
            fontSize={{ base: 'xs', sm: 'sm', md: 'base' }} // Better responsive sizing
            color="var(--color-text)"
            opacity={0.8}
            fontWeight="medium"
            lineHeight="1.2"
            w="full"
            minW={0} // Allow text to wrap
            noOfLines={2} // Limit to 2 lines on mobile
          >
            {title}
          </Text>

          {/* Value */}
          <Flex align="baseline" justify="space-between" w="full" minW={0}>
            {onlineUsers !== null ? (
              <OnlineUsersTooltip onlineUsers={onlineUsers} />
            ) : (
              <Text
                fontSize={{ base: 'xl', sm: '2xl', md: '3xl' }} // Better scaling
                fontWeight="bold"
                letterSpacing="tight"
                color="var(--color-text)"
                lineHeight="1"
                minW={0} // Allow text to shrink
                noOfLines={1}
              >
                {value.toLocaleString()}
              </Text>
            )}
          </Flex>
        </VStack>
      </Box>
    </motion.div>
  );
};

const HomePage = () => {
  const { currentUser, role, userName } = useContext(AuthContext);
  const [dors, setDors] = useState([]);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [userIdNumber, setUserIdNumber] = useState(null);
  

  const [officerSafetyReports, setOfficerSafetyReports] = useState([]);
  const [endOfWeekReports, setEndOfWeekReports] = useState([]);
  const [stats, setStats] = useState({
    totalDORs: 0,
    totalTrainees: 0,
    totalTrainers: 0,
    archivedTrainees: 0,
    totalOfficerSafetyReports: 0,
    totalEndOfWeekReports: 0,
  });
  const [statDeltas, setStatDeltas] = useState({
    totalDORs: 0,
    totalTrainees: 0,
    totalTrainers: 0,
    archivedTrainees: 0,
    totalOfficerSafetyReports: 0,
    totalEndOfWeekReports: 0,
  });
  const prevStatsRef = React.useRef(null);
  const weekStartRef = React.useRef(null);

  // Get current week start (Sunday at 00:00:00)
  const getCurrentWeekStart = useCallback(() => {
    const now = new Date();
    const dayOfWeek = now.getDay(); // 0 is Sunday
    const daysSinceSunday = dayOfWeek;
    
    // Go back to last Sunday (or today if it's Sunday)
    const lastSunday = new Date(now);
    lastSunday.setDate(now.getDate() - daysSinceSunday);
    lastSunday.setHours(0, 0, 0, 0);
    
    return lastSunday.getTime(); // Return as timestamp
  }, []);

  const updateStats = useCallback((newStats) => {
    // Get current week start
    const currentWeekStart = getCurrentWeekStart();
    
    // Check if we need to reset stats (new week or first load)
    const shouldResetStats = !weekStartRef.current || currentWeekStart > weekStartRef.current;
    
    const deltas = {};
    if (shouldResetStats) {
      // Reset deltas when a new week starts
      Object.keys(newStats).forEach(key => {
        deltas[key] = 0;
      });
      // Update week start reference
      weekStartRef.current = currentWeekStart;
      // Store current stats for future delta calculations
      prevStatsRef.current = { ...newStats };
    } else {
      // Calculate deltas based on previous stats
      Object.keys(newStats).forEach((key) => {
        const prev = prevStatsRef.current ? prevStatsRef.current[key] || 0 : 0;
        deltas[key] = newStats[key] - prev;
      });
    }
    
    setStats(newStats);
    setStatDeltas(deltas);
    
    // Only update previous stats when the delta is supposed to reset (weekly)
    // Don't update on every render to keep the delta visible until the next reset
    if (shouldResetStats) {
      prevStatsRef.current = { ...newStats };
    }
    
    // Save week start and previous stats to localStorage for persistence between page loads
    localStorage.setItem('statsWeekStart', weekStartRef.current);
    localStorage.setItem('prevStats', JSON.stringify(prevStatsRef.current));
  }, [getCurrentWeekStart]);

  // Load previous stats and week start from localStorage on component mount
  useEffect(() => {
    const savedWeekStart = localStorage.getItem('statsWeekStart');
    const savedPrevStats = localStorage.getItem('prevStats');
    
    if (savedWeekStart) {
      weekStartRef.current = parseInt(savedWeekStart, 10);
    }
    
    if (savedPrevStats) {
      try {
        prevStatsRef.current = JSON.parse(savedPrevStats);
      } catch (e) {
        console.error('Error parsing saved stats:', e);
        prevStatsRef.current = null;
      }
    }
  }, []);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [users, setUsers] = useState([]);
  const [userPhotoURL, setUserPhotoURL] = useState(null);
  const [sortField, setSortField] = useState('idNumber');
  const [sortDirection, setSortDirection] = useState('asc');
  
  // Pagination for roster
  const [currentPage, setCurrentPage] = useState(1);
  const usersPerPage = 10;


  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedUsers = useMemo(() => {
    return [...users].sort((a, b) => {
      const aValue = (a[sortField] || '').toString().toLowerCase();
      const bValue = (b[sortField] || '').toString().toLowerCase();
      return sortDirection === 'asc' 
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    });
  }, [users, sortField, sortDirection]);

  // Get current page of users
  const currentUsers = useMemo(() => {
    const indexOfLastUser = currentPage * usersPerPage;
    const indexOfFirstUser = indexOfLastUser - usersPerPage;
    return sortedUsers.slice(indexOfFirstUser, indexOfLastUser);
  }, [currentPage, sortedUsers, usersPerPage]);

  // Calculate total pages
  const totalPages = Math.ceil(sortedUsers.length / usersPerPage);

  // Change page
  const nextPage = () => setCurrentPage(prev => Math.min(prev + 1, totalPages));
  const prevPage = () => setCurrentPage(prev => Math.max(prev - 1, 1));

  const fetchReports = async (snapshot) => {
    if (!snapshot) {
      return [];
    }

    const reports = await Promise.all(
      snapshot.docs.map(async (docSnap) => {
        const data = docSnap.data();
        let traineeName = 'Not specified';
        if (data.traineeId) {
          const traineeDoc = await getDoc(doc(db, 'users', data.traineeId));
          if (traineeDoc.exists()) {
            if (traineeDoc.data().archived) {
              return null; // Skip archived trainee
            }
            traineeName = traineeDoc.data().name;
          }
        }

        // Handle date fields
        let processedData = {
          id: docSnap.id,
          ...data,
          traineeName,
        };

        try {
          // Handle End of Week Reports
          if (data.weekEndingDate) {
            processedData.weekEndingDate = data.weekEndingDate instanceof Date 
              ? data.weekEndingDate 
              : data.weekEndingDate.toDate 
                ? data.weekEndingDate.toDate()
                : parseISO(data.weekEndingDate);
          }
          // Handle Officer Safety Reports
          if (data.reportDate) {
            processedData.reportDate = data.reportDate instanceof Date 
              ? data.reportDate 
              : data.reportDate.toDate 
                ? data.reportDate.toDate()
                : parseISO(data.reportDate);
          }
          // Handle DORs
          if (data.date) {
            processedData.date = data.date instanceof Date 
              ? data.date 
              : data.date.toDate 
                ? data.date.toDate()
                : parseISO(data.date);
          }
        } catch (error) {
          console.error('Error processing date:', error, data);
        }

        return processedData;
      })
    );

    // Sort reports by date (most recent first)
    return reports.filter(Boolean).sort((a, b) => {
      const dateA = a.date || a.reportDate || a.weekEndingDate;
      const dateB = b.date || b.reportDate || b.weekEndingDate;
      return dateB - dateA;
    });
  };

  // Memoize fetchDORs function
  /*
  const fetchDORs = useCallback(async (userRole, startAfterDoc = null) => {
    if (!currentUser) return;
    
    try {
      let dorQuery;
      if (userRole === 'trainee') {
        dorQuery = query(
          collection(db, 'dors'),
          where('traineeId', '==', currentUser.uid),
          orderBy('date', 'desc'),
          limit(5)
        );

        // Also fetch OSRs and EOWRs for trainee with limit
        const osrQuery = query(
          collection(db, 'officerSafetyReports'),
          where('traineeId', '==', currentUser.uid),
          orderBy('reportDate', 'desc'),
          limit(5)
        );

        const eowrQuery = query(
          collection(db, 'endOfWeekReports'),
          where('traineeId', '==', currentUser.uid),
          orderBy('weekEndingDate', 'desc'),
          limit(5)
        );

        if (startAfterDoc) {
          // When loading more for trainee, only fetch DORs
          dorQuery = query(dorQuery, startAfter(startAfterDoc));
          const snapshot = await getDocs(dorQuery);
          const fetchedDors = await fetchReports(snapshot);
          setDors(prev => [...prev, ...fetchedDors]);

          if (snapshot.docs.length > 0) {
            setLastDORDoc(snapshot.docs[snapshot.docs.length - 1]);
          } else {
            setLastDORDoc(null);
          }
        } else {
          // Initial load for trainee - fetch all report types
          const [dorSnapshot, osrSnapshot, eowrSnapshot] = await Promise.all([
            getDocs(dorQuery),
            getDocs(osrQuery),
            getDocs(eowrQuery)
          ]);

          const [fetchedDors, fetchedOSRs, fetchedEOWRs] = await Promise.all([
            fetchReports(dorSnapshot),
            fetchReports(osrSnapshot, 'osr'),
            fetchReports(eowrSnapshot, 'eowr')
          ]);

          updateStats({
            totalDORs: fetchedDors.length,
            totalTrainees: 0,
            totalTrainers: 0,
            archivedTrainees: 0,
            totalOfficerSafetyReports: fetchedOSRs.length,
            totalEndOfWeekReports: fetchedEOWRs.length
          });
          
          setDors(fetchedDors);
          setOfficerSafetyReports(fetchedOSRs);
          setEndOfWeekReports(fetchedEOWRs);

          if (dorSnapshot.docs.length > 0) {
            setLastDORDoc(dorSnapshot.docs[dorSnapshot.docs.length - 1]);
          }
        }
      } else if (userRole === 'trainer') {
        dorQuery = query(
          collection(db, 'dors'),
          where('createdBy', '==', currentUser.uid),
          orderBy('date', 'desc'),
          limit(5)
        );
      } else if (userRole === 'admin') {
        dorQuery = query(
          collection(db, 'dors'),
          orderBy('date', 'desc'),
          limit(5)
        );
      }

      if (startAfterDoc && userRole !== 'trainee') {
        dorQuery = query(dorQuery, startAfter(startAfterDoc));
      }

      if (userRole !== 'trainee') {
        const snapshot = await getDocs(dorQuery);
        const fetchedDors = await fetchReports(snapshot);

        if (startAfterDoc) {
          setDors(prev => [...prev, ...fetchedDors]);
        } else {
          setDors(fetchedDors);
        }

        if (snapshot.docs.length > 0) {
          setLastDORDoc(snapshot.docs[snapshot.docs.length - 1]);
        }
      }
    } catch (error) {
      console.error('Error fetching DORs:', error);
      setError('An error occurred while fetching DORs.');
    }
  }, [currentUser]);
  */

  // Initialize presence tracking and subscribe to online users
  useEffect(() => {
    let cleanup = () => {};

    const initializePresenceTracking = async () => {
      if (!currentUser || !role) {
        return;
      }

      if (!auth.currentUser) {
        return;
      }

      try {
        await presenceTracker.startTracking();

        const unsubscribe = presenceTracker.subscribeToOnlineUsers((users) => {
          setOnlineUsers(users || []);
        });

        cleanup = typeof unsubscribe === 'function' ? unsubscribe : () => {};
      } catch (error) {
        console.error('HomePage: Error initializing presence tracking:', error);
        setOnlineUsers([]);
      }
    };

    const timeoutId = setTimeout(initializePresenceTracking, 1000);

    return () => {
      clearTimeout(timeoutId);
      try {
        cleanup();
      } catch (error) {
        console.error('HomePage: Error during cleanup:', error);
      }
    };
  }, [currentUser, role]);

  // Main data fetching effect
  useEffect(() => {
    let isMounted = true;
    const fetchData = async () => {
      // Guard: If user logs out or effect is cleaned up, abort
      if (!currentUser || !role || !isMounted) {
        setLoading(false);
        setUserPhotoURL(defaultAvatar); // Set default if no user
        return;
      }

      setLoading(true);
      setUserPhotoURL(null); // Reset photo while loading
      try {
        // Fetch User Profile Photo and ID Number
        let photoUrlToSet = defaultAvatar;
        let userIdToSet = null;
        try {
          const userDocRef = doc(db, 'users', currentUser.uid);
          const userDocSnap = await getDoc(userDocRef);
          if (userDocSnap.exists()) {
            const userData = userDocSnap.data();
            photoUrlToSet = userData.photoURL || defaultAvatar;
            userIdToSet = userData.idNumber || null;
          }
        } catch (photoError) {
          if (photoError.code === 'permission-denied') {
            // Silently fallback to default avatar on permission error
            photoUrlToSet = defaultAvatar;
            userIdToSet = null;
          } else {
            console.error("Error fetching user data:", photoError);
          }
        }
        setUserPhotoURL(photoUrlToSet);
        setUserIdNumber(userIdToSet);

        // Now fetch reports and other data
      if (!currentUser || !isMounted) return;
      let dorQuery, osrQuery, eowrQuery;

      // Configure queries based on user role
      switch (role) {
          case 'admin':
            // Admin sees all reports
            dorQuery = query(
              collection(db, 'dors'),
              orderBy('date', 'desc')
            );
            osrQuery = query(
              collection(db, 'officerSafetyReports'),
              orderBy('reportDate', 'desc')
            );
            eowrQuery = query(
              collection(db, 'endOfWeekReports'),
              orderBy('weekEndingDate', 'desc')
            );
            break;

          case 'trainer':
            // Trainer sees only reports they created
            dorQuery = query(
              collection(db, 'dors'),
              where('createdBy', '==', currentUser.uid),
              orderBy('date', 'desc')
            );
            osrQuery = query(
              collection(db, 'officerSafetyReports'),
              where('createdBy', '==', currentUser.uid),
              orderBy('reportDate', 'desc')
            );
            eowrQuery = query(
              collection(db, 'endOfWeekReports'),
              where('createdBy', '==', currentUser.uid),
              orderBy('weekEndingDate', 'desc')
            );
            break;

          case 'trainee':
            // Trainee sees only reports about them
            dorQuery = query(
              collection(db, 'dors'),
              where('traineeId', '==', currentUser.uid),
              orderBy('date', 'desc')
            );
            osrQuery = query(
              collection(db, 'officerSafetyReports'),
              where('traineeId', '==', currentUser.uid),
              orderBy('reportDate', 'desc')
            );
            eowrQuery = query(
              collection(db, 'endOfWeekReports'),
              where('traineeId', '==', currentUser.uid),
              orderBy('weekEndingDate', 'desc')
            );
            break;

          default:
            throw new Error('Invalid role');
        }

        // Fetch all reports and users (if admin/trainer)
      if (!currentUser || !isMounted) return;
      const [dorSnapshot, osrSnapshot, eowrSnapshot, usersSnapshot] = await Promise.all([
        getDocs(dorQuery),
        getDocs(osrQuery),
        getDocs(eowrQuery),
        (role === 'admin' || role === 'trainer') ? getDocs(query(collection(db, 'users'))) : Promise.resolve(null) // Fetch users only if needed
      ]);

      if (!isMounted) return;
      const [fetchedDors, fetchedOSRs, fetchedEOWRs] = await Promise.all([
        fetchReports(dorSnapshot),
        fetchReports(osrSnapshot, 'osr'),
        fetchReports(eowrSnapshot, 'eowr')
      ]);

      if (!isMounted) return;
      setDors(fetchedDors);
      setOfficerSafetyReports(fetchedOSRs);
      setEndOfWeekReports(fetchedEOWRs);

      // Process users and stats
      if (usersSnapshot) {
          const fetchedUsers = usersSnapshot.docs
              .map(doc => ({ id: doc.id, ...doc.data() }))
              .filter(user => !user.deactivated && user.active);
          setUsers(fetchedUsers);

          const totalTrainees = fetchedUsers.filter(user => user.role === 'trainee' && !user.archived).length;
          const totalTrainers = fetchedUsers.filter(user => user.role === 'trainer').length;
          const archivedTrainees = fetchedUsers.filter(user => user.role === 'trainee' && user.archived).length;

        updateStats({
            totalDORs: role === 'admin' ? dorSnapshot.size : fetchedDors.length,
          totalTrainees,
          totalTrainers,
          archivedTrainees,
            totalOfficerSafetyReports: role === 'admin' ? osrSnapshot.size : fetchedOSRs.length,
            totalEndOfWeekReports: role === 'admin' ? eowrSnapshot.size : fetchedEOWRs.length
          });
      } else {
          setUsers([]); // Clear users for trainee
           updateStats({
            totalDORs: fetchedDors.length,
            totalTrainees: 0,
            totalTrainers: 0,
            archivedTrainees: 0,
            totalOfficerSafetyReports: fetchedOSRs.length,
            totalEndOfWeekReports: fetchedEOWRs.length
          });
      }

    } catch (error) {
      if (isMounted) {
        if (error.code === 'permission-denied') {
          // Silently handle permission errors after logout
          setError('Failed to fetch data');
          setUserPhotoURL(defaultAvatar);
        } else {
          console.error('Error fetching data:', error);
          setError('Failed to fetch data');
          toast.error('Failed to fetch data');
          setUserPhotoURL(defaultAvatar);
        }
      }
    } finally {
      if (isMounted) setLoading(false);
    }
  };

  fetchData();
  return () => { isMounted = false; };
}, [currentUser, role, userName, updateStats]); // Added updateStats dependency

  const cardStyle = `p-3 sm:p-6 rounded-lg shadow-lg bg-primary-light dark:bg-primary-dark border border-secondary-light dark:border-secondary-dark transition-colors duration-300 text-sm`;

  if (loading && userPhotoURL === null) {
    return (
      <div className="p-4">
        <LoadingSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark text-sm">
        <div className={cardStyle}>
          <p>{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-3 py-1 rounded bg-accent-light dark:bg-accent-dark text-primary-light dark:text-primary-dark hover:bg-accent hover:text-primary-light dark:hover:bg-accent-dark dark:hover:text-primary-dark text-sm transition-colors duration-300"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <Box 
      minH="75vh" 
      p={{ base: 2, sm: 3, md: 4, lg: 6 }} // Better responsive padding
      bg="var(--color-background)"
      color="var(--color-text)"
      w="full"
      maxW="100vw" // Prevent horizontal overflow
      overflowX="hidden" // Hide horizontal scroll
    >
      {/* Global styles for better mobile experience */}
      <style jsx global>{`
        /* Prevent text selection on interactive elements */
        .stat-card-value,
        .stat-card-title,
        button,
        a {
          -webkit-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
          user-select: none;
          -webkit-tap-highlight-color: transparent;
        }
        
        /* Fix for gradient text selection issue */
        [data-text-gradient] {
          -webkit-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
          user-select: none;
          position: relative;
        }
        
        [data-text-gradient]::selection {
          background: transparent !important;
          color: transparent !important;
        }
        
        [data-text-gradient]::-moz-selection {
          background: transparent !important;
          color: transparent !important;
        }
        
        /* Disable highlight on all text elements */
        span, p, h1, h2, h3, h4, h5, h6 {
          -webkit-tap-highlight-color: transparent;
        }
        
        /* Disable text selection highlight globally for stat values */
        *::selection {
          background-color: rgba(0, 173, 181, 0.2);
        }
        
        /* Reduce motion for users who prefer it */
        @media (prefers-reduced-motion: reduce) {
          * {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
        }
        
        /* Safe area padding for mobile devices */
        .px-safe {
          padding-left: env(safe-area-inset-left);
          padding-right: env(safe-area-inset-right);
        }

        /* Prevent horizontal overflow on all elements */
        * {
          box-sizing: border-box;
        }
        
        html, body {
          overflow-x: hidden;
        }
      `}</style>
      
      <Box 
        maxW={{ base: "full", lg: "6xl" }} // Better responsive max width
        mx="auto" 
        w="full"
        px={{ base: 1, sm: 2, md: 4 }} // Responsive horizontal padding
      >
        <VStack spacing={{ base: 4, sm: 5, md: 6 }} w="full" align="stretch">
          {/* Welcome and Stats Card - Refined Styling */}
          <Box 
            borderRadius="xl"
            bg="var(--color-primary)"
            p={{ base: 3, sm: 4, md: 6 }} // Better responsive padding
            boxShadow="0 4px 6px -1px rgba(0, 0, 0, 0.1)"
            w="full"
            overflow="hidden" // Prevent overflow
          >
            {/* Enhanced Header with modern styling */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
            >
              <Flex
                direction={{ base: 'column', lg: 'row' }} // Stack on smaller screens
                align={{ base: 'stretch', lg: 'center' }}
                justify="space-between"
                gap={{ base: 4, md: 6 }}
                mb={{ base: 4, md: 6 }}
                w="full"
              >
                {/* Enhanced Avatar + greeting section */}
                <Flex 
                  align="center" 
                  gap={{ base: 3, md: 4 }} 
                  flex={1}
                  minW={0}
                  w="full"
                >
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Avatar
                      size={{ base: 'lg', md: 'xl' }} // Better responsive sizing
                      name={userName || ''}
                      src={userPhotoURL === defaultAvatar ? undefined : userPhotoURL}
                      border="1px solid"
                      borderColor="var(--color-accent)"
                      bg="var(--color-accent)"
                      color="var(--color-primary-light)"
                      boxShadow="0 4px 12px rgba(0,0,0,0.15)"
                      flexShrink={0}
                      borderRadius="xl"
                    />
                  </motion.div>
                  
                  <Box flex={1} minW={0}>
                    {/* Welcome message with improved mobile layout */}
                    <VStack align="flex-start" spacing={{ base: 1, md: 2 }} w="full">
                      <Flex 
                        direction={{ base: 'column', sm: 'row' }}
                        align={{ base: 'flex-start', sm: 'center' }}
                        gap={{ base: 1, sm: 2 }}
                        w="full"
                        flexWrap="wrap"
                      >
                        <Text
                          fontSize={{ base: 'md', sm: 'lg', md: 'xl' }}
                          fontWeight="bold"
                          color="var(--color-text)"
                          noOfLines={2}
                          minW={0}
                        >
                          Welcome, {userName}
                          {userIdNumber && (
                            <Text as="span" fontSize="sm" opacity={0.8} ml={1}>
                              #{userIdNumber}
                            </Text>
                          )}
                        </Text>
                        {(() => {
                          const rankInfo = getUserRankInfo(userName);
                          if (!rankInfo || !rankInfo.icon) return null;
                          
                          const getColorValue = (color) => {
                            switch(color) {
                              case 'gold': return '#FFD700';
                              case 'silver': return '#C0C0C0';
                              case 'bronze': return '#CD7F32';
                              case 'blue': return 'var(--color-accent)';
                              default: return 'var(--color-accent)';
                            }
                          };
                          
                          return (
                            <Box
                              p={1}
                              borderRadius="md"
                              bg={`${getColorValue(rankInfo.color)}20`}
                              border="1px solid"
                              borderColor={getColorValue(rankInfo.color)}
                              title={rankInfo.title}
                              flexShrink={0}
                            >
                              <Icon 
                                as={rankInfo.icon} 
                                boxSize={4}
                                color={getColorValue(rankInfo.color)}
                              />
                            </Box>
                          );
                        })()}
                      </Flex>
                      
                      <Text
                        fontSize={{ base: 'xs', md: 'sm' }}
                        color="var(--color-text)"
                        opacity={0.7}
                        noOfLines={1}
                      >
                        Ready to make a difference today
                      </Text>
                      
                      {/* Date and time - improved mobile layout */}
                      <Flex 
                        direction={{ base: 'column', sm: 'row' }}
                        gap={{ base: 1, sm: 3 }}
                        align={{ base: 'flex-start', sm: 'center' }}
                        w="full"
                      >
                        <HStack spacing={1} color="var(--color-text)" opacity={0.8}>
                          <Icon as={FiCalendar} boxSize={3.5} />
                          <Text fontSize="xs" fontWeight="medium">
                            {format(new Date(), 'EEEE, MMM d')}
                          </Text>
                        </HStack>
                        <HStack spacing={1} color="var(--color-text)" opacity={0.8}>
                          <Icon as={FiClock} boxSize={3.5} />
                          <Text fontSize="xs" fontFamily="mono">
                            {format(new Date(), 'h:mm a')}
                          </Text>
                        </HStack>
                      </Flex>
                    </VStack>
                  </Box>
                </Flex>

                {/* Enhanced Pills: Holiday + online users - improved mobile layout */}
                <Box w={{ base: 'full', lg: 'auto' }} flexShrink={0}>
                  <Flex
                    direction={{ base: 'row', lg: 'column' }}
                    align="center"
                    justify={{ base: 'space-between', lg: 'flex-end' }}
                    gap={3}
                    w="full"
                  >
                    <motion.div
                      whileHover={{ scale: 1.05 }}
                      transition={{ duration: 0.2 }}
                    >
                      <HolidayCountdown />
                    </motion.div>
                    <motion.div
                      whileHover={{ scale: 1.05 }}
                      transition={{ duration: 0.2 }}
                    >
                      <Flex
                        align="center"
                        px={3}
                        py={2}
                        borderRadius="full"
                        bg="var(--color-secondary)/20"
                        _hover={{
                          bg: 'var(--color-secondary)/30'
                        }}
                        transition="all 0.2s ease"
                      >
                        <OnlineUsersTooltip onlineUsers={onlineUsers} />
                      </Flex>
                    </motion.div>
                  </Flex>
                </Box>
              </Flex>
            </motion.div>

            {/* Enhanced Stats Grid - Fixed for mobile */}
            {role === 'admin' && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <Box 
                  w="full"
                  display="grid"
                  gridTemplateColumns={{
                    base: "repeat(2, 1fr)", // 2 columns on mobile
                    md: "repeat(4, 1fr)"     // 4 columns on desktop
                  }}
                  gap={{ base: 3, sm: 4, md: 6 }}
                  mt={{ base: 4, md: 6 }}
                >
                  <StatCard
                    icon={FiFileText}
                    title="Total DORs"
                    value={stats.totalDORs}
                    delta={statDeltas.totalDORs}
                  />
                  <StatCard
                    icon={FiUsers}
                    title="Active Trainees"
                    value={stats.totalTrainees}
                    delta={statDeltas.totalTrainees}
                  />
                  <StatCard
                    icon={FiShield}
                    title="Total FTOs"
                    value={stats.totalTrainers}
                    delta={statDeltas.totalTrainers}
                  />
                  <StatCard
                    icon={FiArchive}
                    title="Archived Trainees"
                    value={stats.archivedTrainees}
                    delta={statDeltas.archivedTrainees}
                  />
                </Box>
              </motion.div>
            )}
          </Box>

          {/* Reports Overview Card */}
          <ReportsOverview 
            dors={dors}
            officerSafetyReports={officerSafetyReports}
            endOfWeekReports={endOfWeekReports}
          />

          {/* Admin Roster Section */}
          {role === 'admin' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              <Box
                borderRadius="xl"
                overflow="hidden"
                bg="rgba(255, 255, 255, 0.06)"
                backdropFilter="blur(10px)"
                border="1px solid"
                borderColor="rgba(255, 255, 255, 0.08)"
                w="full"
                boxShadow="0 4px 24px rgba(0, 0, 0, 0.04)"
                _dark={{
                  bg: "rgba(255, 255, 255, 0.04)",
                  borderColor: "rgba(255, 255, 255, 0.06)",
                  boxShadow: "0 4px 24px rgba(0, 0, 0, 0.2)"
                }}
              >
                {/* Header */}
                <Box
                  p={{ base: 4, md: 6 }}
                  bg="rgba(255, 255, 255, 0.03)"
                  borderBottom="1px solid"
                  borderColor="rgba(255, 255, 255, 0.06)"
                >
                  <Flex 
                    align="center" 
                    justify="space-between" 
                    direction={{ base: 'column', sm: 'row' }}
                    gap={3}
                  >
                    <HStack spacing={3}>
                      <Box
                        p={2.5}
                        borderRadius="xl"
                        bg="var(--color-accent)/10"
                        color="var(--color-accent)"
                      >
                        <Icon as={FiUsers} boxSize={5} />
                      </Box>
                      <VStack align="start" spacing={0}>
                        <Text
                          fontSize={{ base: 'md', md: 'lg' }}
                          fontWeight="bold"
                          color="var(--color-text)"
                        >
                          Team Roster
                        </Text>
                        <Text
                          fontSize="xs"
                          color="var(--color-text)"
                          opacity={0.6}
                        >
                          Active members in your organization
                        </Text>
                      </VStack>
                    </HStack>
                    <Flex
                      align="center"
                      px={3}
                      py={1.5}
                      borderRadius="full"
                      bg="var(--color-accent)/10"
                      border="1px solid"
                      borderColor="var(--color-accent)/20"
                    >
                      <Text
                        fontSize="sm"
                        fontWeight="semibold"
                        color="var(--color-accent)"
                      >
                        {users.length} {users.length === 1 ? 'member' : 'members'}
                      </Text>
                    </Flex>
                  </Flex>
                </Box>
              
                {/* Table Content */}
                {users.length === 0 && !loading ? (
                  <Box p={{ base: 6, md: 12 }} textAlign="center">
                    <VStack spacing={3}>
                      <Box
                        p={4}
                        borderRadius="full"
                        bg="var(--color-secondary)/20"
                      >
                        <Icon 
                          as={FiUsers} 
                          boxSize={8} 
                          color="var(--color-accent)"
                          opacity={0.5}
                        />
                      </Box>
                      <Text 
                        fontSize="md"
                        fontWeight="medium"
                        color="var(--color-text)"
                        opacity={0.8}
                      >
                        No team members found
                      </Text>
                    </VStack>
                  </Box>
                ) : (
                  <Box p={{ base: 2, md: 6 }} overflowX="auto">
                    <TableContainer>
                      <Table variant="unstyled" size="sm">
                        <Thead>
                          <Tr>
                            {[
                              { key: 'name', label: 'Name', icon: FiUser, showAlways: true },
                              { key: 'idNumber', label: 'ID', icon: FiShield, showAlways: true },
                              { key: 'email', label: 'Email', icon: FiFileText, showAlways: false }
                            ].map(({ key, label, icon, showAlways }) => (
                              <Th
                                key={key}
                                cursor="pointer"
                                onClick={() => handleSort(key)}
                                display={{ base: showAlways ? 'table-cell' : 'none', md: 'table-cell' }}
                                py={3}
                                px={{ base: 2, md: 4 }}
                                color="var(--color-text)"
                                opacity={0.7}
                                fontSize="xs"
                                fontWeight="semibold"
                                textTransform="uppercase"
                                letterSpacing="wider"
                                borderBottom="2px solid"
                                borderColor="rgba(255, 255, 255, 0.06)"
                                _hover={{
                                  color: 'var(--color-accent)',
                                  opacity: 1
                                }}
                                transition="all 0.2s"
                              >
                                <HStack spacing={2}>
                                  <Icon as={icon} boxSize={3} />
                                  <Text>{label}</Text>
                                  {sortField === key && (
                                    <motion.div
                                      initial={{ rotate: 0 }}
                                      animate={{ rotate: sortDirection === 'asc' ? 0 : 180 }}
                                      transition={{ duration: 0.2 }}
                                    >
                                      <Icon
                                        as={FiChevronUp}
                                        boxSize={3}
                                        color="var(--color-accent)"
                                      />
                                    </motion.div>
                                  )}
                                </HStack>
                              </Th>
                            ))}
                          </Tr>
                        </Thead>
                        <Tbody>
                          <AnimatePresence>
                            {currentUsers.map((user, index) => (
                              <motion.tr
                                key={user.id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                transition={{ duration: 0.3, delay: index * 0.05 }}
                              >
                                <Td 
                                  py={3}
                                  px={{ base: 2, md: 4 }}
                                  borderBottom="1px solid"
                                  borderColor="rgba(255, 255, 255, 0.04)"
                                >
                                  <HStack spacing={3}>
                                    <Avatar
                                      size="sm"
                                      name={user.name || ''}
                                      src={user.photoURL === defaultAvatar ? undefined : user.photoURL}
                                      bg="var(--color-accent)"
                                      color="white"
                                    />
                                    <VStack align="start" spacing={0} minW={0}>
                                      <Text 
                                        fontWeight="semibold"
                                        fontSize="sm"
                                        color="var(--color-text)"
                                        noOfLines={1}
                                      >
                                        {user.name || 'N/A'}
                                      </Text>
                                      <Text
                                        fontSize="xs"
                                        color="var(--color-text)"
                                        opacity={0.6}
                                        noOfLines={1}
                                      >
                                        {user.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : 'Member'}
                                      </Text>
                                    </VStack>
                                  </HStack>
                                </Td>
                                <Td 
                                  py={3}
                                  px={{ base: 2, md: 4 }}
                                  borderBottom="1px solid"
                                  borderColor="rgba(255, 255, 255, 0.04)"
                                >
                                  <Text 
                                    fontSize="sm" 
                                    fontWeight="medium"
                                    color="var(--color-text)"
                                  >
                                    {user.idNumber || 'N/A'}
                                  </Text>
                                </Td>
                                <Td 
                                  display={{ base: 'none', md: 'table-cell' }}
                                  py={3}
                                  px={{ base: 2, md: 4 }}
                                  borderBottom="1px solid"
                                  borderColor="rgba(255, 255, 255, 0.04)"
                                >
                                  <Text
                                    fontSize="sm"
                                    color="var(--color-accent)"
                                    noOfLines={1}
                                  >
                                    {user.email || 'N/A'}
                                  </Text>
                                </Td>
                              </motion.tr>
                            ))}
                          </AnimatePresence>
                        </Tbody>
                      </Table>
                    </TableContainer>
                    
                    {/* Pagination */}
                    {totalPages > 1 && (
                      <Box 
                        mt={6}
                        pt={4}
                        borderTop="1px solid"
                        borderColor="rgba(255, 255, 255, 0.06)"
                      >
                        <HStack justify="center" spacing={4}>
                          <Button
                            size="sm"
                            variant="ghost"
                            isDisabled={currentPage === 1}
                            onClick={prevPage}
                            leftIcon={<FiChevronLeft />}
                            color="var(--color-text)"
                            bg="transparent"
                            _hover={{
                              bg: 'var(--color-secondary)/30',
                              color: 'var(--color-accent)'
                            }}
                            _disabled={{
                              opacity: 0.4,
                              cursor: 'not-allowed',
                              color: 'var(--color-text)',
                              _hover: {
                                bg: 'transparent'
                              }
                            }}
                            border="1px solid"
                            borderColor="rgba(255, 255, 255, 0.1)"
                            _dark={{
                              borderColor: "rgba(255, 255, 255, 0.2)"
                            }}
                          >
                            Previous
                          </Button>
                          
                          <Text fontSize="sm" color="var(--color-text)">
                            Page {currentPage} of {totalPages}
                          </Text>
                          
                          <Button
                            size="sm"
                            variant="ghost"
                            isDisabled={currentPage === totalPages}
                            onClick={nextPage}
                            rightIcon={<FiChevronRight />}
                            color="var(--color-text)"
                            bg="transparent"
                            _hover={{
                              bg: 'var(--color-secondary)/30',
                              color: 'var(--color-accent)'
                            }}
                            _disabled={{
                              opacity: 0.4,
                              cursor: 'not-allowed',
                              color: 'var(--color-text)',
                              _hover: {
                                bg: 'transparent'
                              }
                            }}
                            border="1px solid"
                            borderColor="rgba(255, 255, 255, 0.1)"
                            _dark={{
                              borderColor: "rgba(255, 255, 255, 0.2)"
                            }}
                          >
                            Next
                          </Button>
                        </HStack>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            </motion.div>
          )}
        </VStack>
      </Box>
      
      {/* Add bottom spacing */}
      <Box pb={{ base: 6, md: 8 }} />
    </Box>
  );
};

export default HomePage;
